import { gql } from '@apollo/client'
import {
  GetDetailedTableQuery,
  GetSettingsPageTableDetailsQuery,
  GetTableChildAndBacklinkTablesQuery,
  GetTableRowsQuery,
  GetTablesQuery,
  GetTablesSimpleQuery,
} from '~/common/generated/apollo-operations'
import { EnumSetFragment } from '~/components/shared/column-fragments'

// GetDetailedTableQuery types
export type DetailedTable = GetDetailedTableQuery['table']
export type DetailedChildTable = GetTableChildAndBacklinkTablesQuery['table']['childTables'][number]
export type DetailedTableColumn = GetDetailedTableQuery['table']['columns'][number]
export type DetailedTableTab = GetDetailedTableQuery['table']['tableTabs'][number]
export type BasicChartInTable = GetDetailedTableQuery['table']['charts'][number]
export type DetailedTableColumnFilter = DetailedTableTab['filters'][number]
export type DetailedTableTabColumn = DetailedTableTab['columns'][number]
export type DetailedTableLookupColumn =
  GetDetailedTableQuery['table']['columns'][number]['lookupColumn']

// GetTableRowsQuery types
export type DetailedTableRow = GetTableRowsQuery['paginatedTableRows']['rows'][number]

export type DetailedTableColumnWithTable = DetailedTableColumn & {
  table: { id: string; name: string }
}

export type DetailedTableCell = DetailedTableRow['cells'][number]
export type DossTableCell = Omit<DetailedTableCell, 'column'> & { column: DetailedTableColumn }
export type DossTableRow = Omit<DetailedTableRow, 'cells'> & { cells: DossTableCell[] }
export type DetailedLookupTableCell = DetailedTableCell['lookupCells'][number]

export enum TableType {
  TABLE = 'table',
  CHILD_TABLE = 'childTable',
  BACKLINK_TABLE = 'backlinkTable',
  CHART = 'chart',
}

// GetSettingsPageTableDetails
export type SettingsPageTable = GetSettingsPageTableDetailsQuery['table']
export type SettingsPageColumn = GetSettingsPageTableDetailsQuery['table']['columns'][number]
export type SettingsPageTableIcon = GetSettingsPageTableDetailsQuery['table']['icon']
export type SettingsPageParentTable = GetSettingsPageTableDetailsQuery['table']['parentTable']
export type SettingsPageChildTable =
  GetSettingsPageTableDetailsQuery['table']['childTables'][number]

// Types for Table CSV operations
export type CsvColumn = Pick<
  SettingsPageColumn,
  | 'id'
  | 'name'
  | 'order'
  | 'type'
  | 'innerType'
  | 'isNullable'
  | 'enumOptions'
  | 'indexType'
  | 'indexPrefix'
  | 'indexSuffix'
  | 'indexAutogenerate'
>
export type CsvTable = Pick<SettingsPageTable, 'id' | 'name' | 'parentTable'> & {
  columns: CsvColumn[]
  childTables: Array<Pick<SettingsPageChildTable, 'id' | 'name'> & { columns: CsvColumn[] }>
}

type SettingsPageColumnNonNullable = {
  [K in keyof SettingsPageColumn]: NonNullable<SettingsPageColumn[K]>
}
export type SettingsPageLookupGroup = SettingsPageColumnNonNullable['lookupColumnGroup']
export type SettingsPageLookupGroupColumn = SettingsPageLookupGroup['columns'][number]

export type SettingsPageGroup = GetSettingsPageTableDetailsQuery['table']['columnGroups'][number]
export type SettingsPageGroupMember =
  GetSettingsPageTableDetailsQuery['table']['columnGroups'][number]['columns'][number]

export type StandardTable = GetTablesQuery['tables'][number]

export type SimpleTable = GetTablesSimpleQuery['tables'][number]

export const newTableColumnFragment = gql`
  fragment TableWithColumnsFragment on Table {
    id
    columns {
      id
      name
      description
      order
      type
      innerType
      isNullable
      isReadOnly
      isDecimal
      indexType
      indexPrefix
      indexSuffix
      indexAutogenerate
      enumOptions
      ...EnumSetFields
      displayImagesInline
      formula {
        id
        formulaString
      }
      lookupColumnGroup {
        id
        columns {
          id
          name
          type
          innerType
          lookupColumn {
            id
            name
          }
        }
        lookupTable {
          id
          name
          icon {
            id
            name
          }
        }
      }
      lookupColumn {
        id
        type
      }
    }
  }
  ${EnumSetFragment}
`

export const newTableTabColumnFragment = gql`
  fragment TableTabWithColumns on TableTab {
    id
    columns {
      id
      order
      isFrozen
      systemGeneratedColumnType
      tableColumn {
        id
        order
      }
    }
  }
`
